/**
 * CORE-AB — the basis a candidate was actually ranked on.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ITEM
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-AB's Missing Constitutional Behavior is one line — "that the displayed
 * basis match the actual ranking basis" — and its scorecard carries exactly one
 * ✗ of eleven for it. **CORE-AB-GAP-001**: *"displayed score is not the ranking
 * basis … a number implies a reason it did not provide."*
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THE RANKER ACTUALLY DOES
 * ═══════════════════════════════════════════════════════════════════════════
 * `smart-assign` sorts LEXICOGRAPHICALLY. Not a weighted total — a strict
 * priority ladder, where a later key is consulted only when every earlier one
 * ties:
 *
 *   1. proficiency BAND        higher wins        ← decides most placements
 *   2. current workload        lower wins         only when the bands tie
 *   3. earliest start (EFS)    earlier wins       only when 1 and 2 tie
 *   4. historical affinity     higher wins        only when 1–3 tie
 *   5. rank tie-break          higher wins        a last resort; see below
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE FIFTH KEY IS NOT THE NUMBER ON THE CARD (corrected 2026-09-11)
 * ═══════════════════════════════════════════════════════════════════════════
 * The first version of this module read `score` for key 5 and was wrong in a
 * way that mattered.
 *
 * The comparator's last resort IS `b.score - a.score` — but at compare time
 * `score` still holds the PRE-sort weighted value. The post-sort pass then
 * overwrites that same field with the monotonic presentation number, which by
 * construction differs between every adjacent pair (it steps down by at least
 * 1). So a reader comparing the overwritten `score` finds a difference ALWAYS,
 * and reports a fifth-key decision even for a true five-key dead heat.
 *
 * The case that was supposed to cover this passed only because it hand-built
 * candidates with equal `score` — a shape the real payload cannot produce once
 * the post-sort pass has run. A vacuous proof of the one key it existed for.
 *
 * `smart-assign` now preserves the compared value as `rankTieBreakScore`
 * before the overwrite, and this module reads THAT. Where it is absent the
 * answer is UNDETERMINED — not a dead heat, and not a decision. Knowing the
 * first four keys tied is not the same as knowing what settled the order, and
 * substituting the presentation number for the missing evidence is exactly the
 * mistake being corrected.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THE SCREEN SHOWED INSTEAD
 * ═══════════════════════════════════════════════════════════════════════════
 * A single "Overall Score" of 60–100, rendered as the headline number, which is
 * neither of the two things a reader would take it for.
 *
 * It is first computed as a WEIGHTED SUM — proficiency 40, workload 25,
 * affinity 20, scheduling fit ±5, bay bonus 5, minus a tier-gap penalty — a
 * formula the ranking never uses. Then, POST-SORT, it is overwritten so it
 * decreases monotonically with rank position, anchored on the top candidate's
 * proficiency with steps taken from proficiency gaps.
 *
 * The result is a number that ALWAYS AGREES WITH THE ORDER and never explains
 * it. The frozen audit's phrase is exact: *"honest ordering, misleading
 * number."* A reader comparing two candidates sees 91 and 87 and concludes the
 * first is four points better on some blend of the four metrics beside it —
 * when what actually happened may be that they are in the same proficiency band
 * and one of them had twenty fewer minutes booked.
 *
 * Three of the four real keys make this worse in a specific way:
 *
 *   - PROFICIENCY IS BANDED. The screen shows `proficiencyMatch` as a
 *     percentage, implying it contributes continuously. It does not: 82% and
 *     84% are the same band and rank identically, while 84% and 85% are
 *     different bands and one beats the other outright.
 *   - EARLIEST START and HISTORICAL AFFINITY decide placements and appear
 *     nowhere on the card at all.
 *   - AVAILABLE TIME and BAY MATCH appear prominently on the card and are NOT
 *     ranking keys. Bay compatibility is a filter — it decides who is on the
 *     list, not who is above whom.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS MODULE DOES, AND WHAT IT DELIBERATELY LEAVES ALONE
 * ═══════════════════════════════════════════════════════════════════════════
 * It turns a ranking row into the ordered account of the keys that actually
 * decided it, and — for any candidate below the top — names the single key that
 * put it there.
 *
 * It is PURE and changes no ranking. The order smart-assign produced is the
 * order; this only describes it.
 *
 * It does NOT remove or rename the displayed score. The capability's own open
 * questions list "whether the displayed score should be removed, renamed, or
 * replaced with the rank keys themselves" as an unresolved PRODUCT decision,
 * and quietly picking one while closing a different item would be answering a
 * question nobody asked this slice to answer. The additive reading is taken
 * instead: the number stays, and the basis is stated beside it, so the
 * displayed basis IS the ranking basis whichever way that decision later goes.
 */

/** The keys `smart-assign` sorts on, in the order it consults them. */
export type RankKey =
  | 'proficiency_band'
  /**
   * Core-100 R5 (CORE-BI x CORE-I) — a CONDITIONAL rung, directly after the
   * band: consulted only when the shop enabled "Weigh demonstrated diagnostic
   * competency" and the work is diagnostic, which the server signals by sending
   * a numeric `diagnosticCompetencyBand` on every candidate. Not in
   * RANK_KEY_ORDER (the always-on ladder); see `rankKeyOrderFor`.
   */
  | 'diagnostic_competency'
  | 'current_workload'
  | 'earliest_start'
  | 'historical_affinity'
  | 'rank_tie_break';

/** The lexicographic ladder, in decision order. */
export const RANK_KEY_ORDER: readonly RankKey[] = [
  'proficiency_band',
  'current_workload',
  'earliest_start',
  'historical_affinity',
  'rank_tie_break',
] as const;

export const RANK_KEY_LABEL: Readonly<Record<RankKey, string>> = Object.freeze({
  proficiency_band: 'Proficiency band',
  diagnostic_competency: 'Demonstrated diagnostic competency',
  current_workload: 'Current workload',
  earliest_start: 'Earliest start',
  historical_affinity: 'Worked on this before',
  rank_tie_break: 'Tie-break',
});

/**
 * The subset of a ranking row this module needs. Every field is one the ranker
 * actually sorts on — a field that is not a rank key has no business here,
 * because including one is how the card came to imply a blend in the first
 * place.
 */
export interface RankableCandidate {
  technicianName?: string;
  /** The BAND, not the percentage. Higher wins. */
  proficiencyBand?: number | null;
  /** The percentage, carried only to explain what the band was derived from. */
  proficiencyMatch?: number | null;
  /**
   * Roadmap #8: false when OptiBay has no proficiency history in these
   * categories. The ranker then places the candidate at the neutral position
   * (neither bonus nor penalty) — the band is that position, not a score.
   */
  proficiencyKnown?: boolean | null;
  /**
   * Core-100 R5 (CORE-BI x CORE-I) — the diagnostic rung, a number only when
   * the shop's preference was in force for this ranking. Higher wins.
   */
  diagnosticCompetencyBand?: number | null;
  /** The demonstrated evidence that rung rests on (null = no established record). */
  diagnosticCompetency?: {
    score: number;
    sampleStatus: string;
    scoredSampleSize: number;
    categoryKey: string | null;
  } | null;
  /** Minutes already booked. Lower wins. */
  currentWorkload?: number | null;
  /** Minutes from midnight of the suggested start. For DISPLAY. */
  startMinutes?: number | null;
  /**
   * FLOYD-7 — the shop-local DATE the start falls on, and whether that is a
   * later day than the one requested.
   *
   * The ranking compares `startSortKey` (chronological). `startMinutes` is a
   * clock face and says nothing about which day, which is how "7:30 against
   * 19:07" came to describe tomorrow morning as EARLIER than this evening.
   * Absent on older payloads, and absent means "same day, as before".
   */
  startDateStr?: string | null;
  isFutureDay?: boolean | null;
  /**
   * The chronological key the ranker actually sorted on (days x 1440 +
   * minute). This module decides the basis from THIS, not from the clock
   * face, so the named basis is the one that decided.
   */
  startSortKey?: number | null;
  /** Higher wins. */
  historicalAffinityScore?: number | null;
  /**
   * The PRE-sort value the comparator's fifth key compared, preserved by
   * `smart-assign` before the post-sort pass rewrites `score`.
   *
   * Absent means the fifth key cannot be read — NOT that it tied.
   */
  rankTieBreakScore?: number | null;
  /**
   * The number on the card. Post-sort, monotonic with position, and NOT a rank
   * key by the time anything here sees it. Carried only so the module can
   * refuse to use it.
   */
  score?: number | null;
}

export interface RankKeyReading {
  key: RankKey;
  label: string;
  /** This candidate's value, formatted for reading. */
  value: string;
  /** True where this key is the one that settled this candidate's position. */
  decisive: boolean;
}

/**
 * Why no key could be named, when none could.
 *
 *   dead_heat      every key including the preserved fifth was equal. A real
 *                  finding: the order between these two is whatever the sort
 *                  was stable on.
 *   undetermined   the first four tied and the preserved fifth key is not
 *                  available, so what settled the order cannot be read. NOT a
 *                  dead heat — substituting the post-sort presentation number
 *                  here is precisely the error this module was corrected for.
 */
export type RankTieState = 'dead_heat' | 'undetermined';

export interface RankingBasis {
  /** The ladder, with this candidate's values, in the order it is consulted. */
  keys: RankKeyReading[];
  /** The key that placed this candidate below the one above it. */
  decidedBy: RankKey | null;
  /**
   * Set only when `decidedBy` is null and this is not the top candidate —
   * whether that is a genuine dead heat or an unreadable fifth key.
   */
  tieState: RankTieState | null;
  /** One sentence naming it, or null for the top candidate. */
  explanation: string | null;
  /** True for the candidate nothing sits above. */
  isTop: boolean;
  /**
   * True for the comeback technician held at #1 by the override. Their
   * position is not a ranking result and no key decided it.
   */
  pinned: boolean;
}

const num = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/** `540` → `9:00`. Returns null for the ranker's 9999 "no start" sentinel. */
export function formatStartMinutes(v: number | null | undefined): string | null {
  const m = num(v);
  if (m === null || m >= 24 * 60) return null;
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  return `${h}:${mm}`;
}

/**
 * The ladder as it ran for this candidate: the always-on five keys, with the
 * diagnostic rung after the band when (and only when) the server sent it.
 */
export function rankKeyOrderFor(...candidates: Array<RankableCandidate | null | undefined>): readonly RankKey[] {
  const carries = candidates.length > 0
    && candidates.every((c) => !!c && num(c.diagnosticCompetencyBand) !== null);
  return carries
    ? [RANK_KEY_ORDER[0], 'diagnostic_competency', ...RANK_KEY_ORDER.slice(1)]
    : RANK_KEY_ORDER;
}

function readingFor(c: RankableCandidate, key: RankKey): string {
  switch (key) {
    case 'diagnostic_competency': {
      const band = num(c.diagnosticCompetencyBand);
      if (band === null) return 'not used';
      const d = c.diagnosticCompetency;
      if (!d) return `band ${band} (no established diagnostic record yet)`;
      return `band ${band} (${Math.round(d.score)}, ${d.scoredSampleSize} scored diagnoses, ${d.sampleStatus})`;
    }
    case 'proficiency_band': {
      const band = num(c.proficiencyBand);
      if (band === null) return 'not recorded';
      if (c.proficiencyKnown === false) return `band ${band} (no OptiBay history yet — neutral)`;
      const pct = num(c.proficiencyMatch);
      // The band is what ranks. The percentage is shown as its source, in
      // parentheses, so nobody reads the percentage as the thing being compared.
      return pct === null ? `band ${band}` : `band ${band} (${Math.round(pct)}%)`;
    }
    case 'current_workload': {
      const w = num(c.currentWorkload);
      return w === null ? 'not recorded' : `${Math.round(w)} min booked`;
    }
    case 'earliest_start': {
      const t = formatStartMinutes(c.startMinutes);
      if (t === null) return 'no start time';
      // A time alone is only honest while both candidates are on the same
      // day. When this one is not, the day travels with the number.
      return c.isFutureDay ? `${t} on ${c.startDateStr ?? 'a later day'}` : t;
    }
    case 'historical_affinity': {
      const a = num(c.historicalAffinityScore);
      return a === null ? 'not recorded' : String(Math.round(a));
    }
    case 'rank_tie_break': {
      // Deliberately NOT `c.score`. See the header.
      const s = num(c.rankTieBreakScore);
      return s === null ? 'not recorded' : String(Math.round(s));
    }
  }
}

/**
 * Which key separates `candidate` from `above`, following the ranker's own
 * ladder and stopping at the first key that differs.
 *
 * Returns null when every key ties — a genuine dead heat, where the order
 * between them is whatever the sort was stable on and no key can be named. That
 * is worth reporting as itself rather than attributing the position to the last
 * key looked at.
 */
function firstDifferingKey(
  candidate: RankableCandidate,
  above: RankableCandidate,
): RankKey | null {
  for (const key of rankKeyOrderFor(candidate, above)) {
    switch (key) {
      case 'proficiency_band':
        if (num(candidate.proficiencyBand) !== num(above.proficiencyBand)) return key;
        break;
      case 'diagnostic_competency':
        if (num(candidate.diagnosticCompetencyBand) !== num(above.diagnosticCompetencyBand)) return key;
        break;
      case 'current_workload':
        if (num(candidate.currentWorkload) !== num(above.currentWorkload)) return key;
        break;
      case 'earliest_start': {
        // FLOYD-7 — compare what the RANKER compared. `startMinutes` is a
        // clock face: two candidates starting 07:30 on different days read as
        // tied here while the server had already used the difference to order
        // them, and the basis named would be a key that never decided.
        // `startSortKey` is chronological; older payloads without it fall
        // back to the clock, which is what they always were.
        const mine = num(candidate.startSortKey) ?? num(candidate.startMinutes);
        const theirs = num(above.startSortKey) ?? num(above.startMinutes);
        if (mine !== theirs) return key;
        break;
      }
      case 'historical_affinity':
        if (num(candidate.historicalAffinityScore) !== num(above.historicalAffinityScore))
          return key;
        break;
      case 'rank_tie_break': {
        // The preserved pre-sort value, or nothing. Two absent values are not
        // a tie here — see `fifthKeyReadable` at the call site.
        const mine = num(candidate.rankTieBreakScore);
        const theirs = num(above.rankTieBreakScore);
        // BOTH must be present. One number against nothing is not a
        // comparison, and calling it a difference would name a decision that
        // was never made — the caller reports it as undetermined instead.
        if (mine !== null && theirs !== null && mine !== theirs) return key;
        break;
      }
    }
  }
  return null;
}

function explain(
  key: RankKey,
  candidate: RankableCandidate,
  above: RankableCandidate,
): string {
  const them = above.technicianName?.trim() || 'the candidate above';
  switch (key) {
    case 'diagnostic_competency':
      return `Same proficiency band as ${them}; ranked below on demonstrated diagnostic competency (your shop weighs it for diagnostic work) — ${readingFor(above, key)} against ${readingFor(candidate, key)}.`;
    case 'proficiency_band':
      // Named as a band, not a percentage, because that is what was compared.
      return `Ranked below ${them} on proficiency band — ${readingFor(above, key)} against ${readingFor(candidate, key)}.`;
    case 'current_workload':
      return `Same proficiency band as ${them}; ranked below on current workload — ${readingFor(above, key)} against ${readingFor(candidate, key)}.`;
    case 'earliest_start': {
      // FLOYD-7 — "earlier" is a claim about time, and it is only true when
      // both starts are on the same day. Floyd's was told a technician who
      // could not work until tomorrow morning "can start earlier" than one
      // already on shift that evening.
      const crossesDays =
        !!candidate.startDateStr && !!above.startDateStr
        && candidate.startDateStr !== above.startDateStr;
      if (crossesDays) {
        return `Tied with ${them} on proficiency band and workload; ranked below on `
          + `earliest start — ${readingFor(above, key)} against `
          + `${readingFor(candidate, key)}.`;
      }
      return `Tied with ${them} on proficiency band and workload; ranked below because they can start earlier (${readingFor(above, key)} against ${readingFor(candidate, key)}).`;
    }
    case 'historical_affinity':
      return `Tied with ${them} on band, workload and start time; ranked below on having worked on this vehicle or job before.`;
    case 'rank_tie_break':
      // A real fifth-key decision, read from the PRESERVED pre-sort value.
      // "Scored lower" would dress a last resort as a basis, so it is named as
      // the tie-break it is.
      return `Tied with ${them} on proficiency band, workload, start time and history; ranked below on the final tie-break.`;
  }
}

/**
 * Describe why this candidate sits where it does.
 *
 * `above` is the candidate immediately above it in the SAME ranked list —
 * adjacency matters, because a lexicographic position is settled against the
 * neighbour, not against the field.
 */
export function describeRankingBasis(
  candidate: RankableCandidate,
  above?: RankableCandidate | null,
  options?: { pinned?: boolean },
): RankingBasis {
  const decidedBy = above && !options?.pinned ? firstDifferingKey(candidate, above) : null;

  // Whether the fifth key can be read at all. Both values must be present:
  // one present and one absent is not a comparison, and treating it as a tie
  // would invent an equality between a number and nothing.
  const fifthKeyReadable =
    !!above &&
    num(candidate.rankTieBreakScore) !== null &&
    num(above.rankTieBreakScore) !== null;

  const keys: RankKeyReading[] = rankKeyOrderFor(candidate).map((key) => ({
    key,
    label: RANK_KEY_LABEL[key],
    value: readingFor(candidate, key),
    decisive: key === decidedBy,
  }));

  // The pin is an override, not a placement. No key produced it, so none is
  // named, and it is not reported as "ranked first" either — it did not win.
  if (options?.pinned) {
    return {
      keys,
      decidedBy: null,
      tieState: null,
      explanation: COMEBACK_PIN_EXPLANATION,
      isTop: false,
      pinned: true,
    };
  }

  if (!above) {
    return {
      keys,
      decidedBy: null,
      tieState: null,
      explanation: null,
      isTop: true,
      pinned: false,
    };
  }

  const them = above.technicianName?.trim() || 'the candidate above';

  if (decidedBy) {
    return {
      keys,
      decidedBy,
      tieState: null,
      explanation: explain(decidedBy, candidate, above),
      isTop: false,
      pinned: false,
    };
  }

  if (fifthKeyReadable) {
    // Every key equal, INCLUDING the preserved fifth. A genuine dead heat.
    return {
      keys,
      decidedBy: null,
      tieState: 'dead_heat',
      explanation:
        `Tied with ${them} on every ranking key. The order between them was not decided by anything measured about either.`,
      isTop: false,
      pinned: false,
    };
  }

  // The first four tied and the fifth cannot be read. Saying "dead heat" here
  // would claim a finding; saying "tie-break" would claim a decision. Neither
  // is known.
  return {
    keys,
    decidedBy: null,
    tieState: 'undetermined',
    explanation:
      `Tied with ${them} on proficiency band, workload, start time and history. What settled the order between them was not recorded.`,
    isTop: false,
    pinned: false,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// COMEBACK OVERRIDE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PRODUCT RULING. On a comeback the technician who performed the original
 * work is deliberately placed first. That override affects ONLY that
 * technician's position: every other technician keeps the exact relative
 * order smart-assign returned.
 *
 * This is an extraction, not a sort. Nothing here reads a rank key or a
 * score, so no display value — tied, absent or nonsense — can disturb the
 * order of the technicians the pin did not touch. It previously was a
 * comparator re-sorting the remainder by the post-sort display artefact,
 * which discarded band, workload and earliest-start for everyone below the
 * pin, and which also added 10 to the pinned technician's `score` — a value
 * the browser writes to assignment_audit as their fitness_score.
 */
export function applyComebackPin<T extends { technicianId: string }>(
  rankings: T[],
  pinnedTechnicianId: string | null | undefined,
): T[] {
  if (!pinnedTechnicianId) return rankings;
  const pinned = rankings.find((r) => r.technicianId === pinnedTechnicianId);
  if (!pinned) return rankings;
  return [pinned, ...rankings.filter((r) => r.technicianId !== pinnedTechnicianId)];
}

export const COMEBACK_PIN_EXPLANATION =
  'Placed first because this is a comeback and they performed the original '
  + 'work. That position is an override, not a ranking result — no ranking '
  + 'key put them here. Everyone below is in normal ranking order.';

/**
 * The candidate a given position was settled against.
 *
 * Adjacency is what a lexicographic position is decided by, but the PIN is
 * not a ranking result, so nobody is described as having been placed below
 * it. This walks up past the pinned technician: the first unpinned candidate
 * is compared against nothing, exactly as it would be with the pin absent.
 */
export function rankingNeighbour<T extends { technicianId: string }>(
  rankings: T[],
  index: number,
  pinnedTechnicianId?: string | null,
): T | null {
  for (let i = index - 1; i >= 0; i--) {
    if (pinnedTechnicianId && rankings[i].technicianId === pinnedTechnicianId) continue;
    return rankings[i];
  }
  return null;
}

/**
 * What the displayed score is, said plainly.
 *
 * Exported as one string so the modal and the tests agree, and so a copy review
 * can find it. It must not read as a compliment or a measurement — it is a
 * position indicator, and its only guaranteed property is that it decreases
 * with rank.
 */
export const DISPLAY_SCORE_CAVEAT =
  'A position indicator, not the ranking basis — it is assigned after sorting so it always decreases down the list.';

export const DISPLAY_SCORE_LABEL = 'Rank indicator';

/**
 * Founder field test 2026-10-16: a number beside a candidate with no OptiBay
 * history reads as a measured performance figure when it is only list position
 * (with every proficiency unknown the list reads 79, 78, 77…). Such a candidate
 * shows this text instead. Order and explanations are unchanged.
 */
export const NO_HISTORY_INDICATOR = 'No history';
export const NO_HISTORY_CAVEAT =
  'No OptiBay work history for this job yet — the list order comes from availability and fit, not a measured score.';

export type RankIndicatorDisplay =
  | { kind: 'position'; value: number }
  | { kind: 'no_history' };

/** What the candidate card shows in the indicator slot. Unknown proficiency is never a number. */
export function rankIndicatorDisplay(ranking: { score: number; proficiencyMatch: number | null | undefined }): RankIndicatorDisplay {
  if (ranking.proficiencyMatch == null) return { kind: 'no_history' };
  return { kind: 'position', value: ranking.score };
}

/**
 * Things the card shows that do NOT decide placement.
 *
 * Available time and bay match are prominent on the candidate card and neither
 * is a ranking key. Bay compatibility is a FILTER — it decides who appears at
 * all, not who appears above whom — and available time informs the suggested
 * start rather than the order.
 */
export const NON_RANKING_METRICS = Object.freeze([
  'Available Time',
  'Bay Match',
] as const);

// ═══════════════════════════════════════════════════════════════════════════
// EXCLUSIONS — a hard constraint filters, it does not score
// ═══════════════════════════════════════════════════════════════════════════
//
// Bay compatibility is named above as a filter rather than a rank key. This
// is the other half of saying so: the technicians a filter removed are
// reported as removed, and why, instead of being silently absent. Absent from
// a list and last in a list are different facts about a person.

export type ExclusionReason =
  | 'NOT_CONSIDERED'
  | 'NO_SCHEDULABLE_TIME'
  | 'BAY_INCOMPATIBLE'
  | 'START_IN_PAST'
  | 'PROFICIENCY_GAP'
  | 'NOT_RETURNED';

export interface Exclusion {
  technicianId: string;
  technicianName?: string;
  reason: ExclusionReason;
  /**
   * smart-assign's own words where it gave them, kept verbatim rather than
   * paraphrased — the server is the authority on why it rejected somebody.
   */
  explanation: string;
}

/**
 * An audit row as smart-assign emits it (`allTechsForAudit`). Snake_case, NOT
 * the camelCase ranking shape. The modal falls back to `data.rankings` when
 * auditData is absent, so both shapes are accepted.
 */
export interface ConsideredTechnician {
  tech_id?: string;
  tech_name?: string;
  rejection_reason?: string | null;
  technicianId?: string;
  technicianName?: string;
}

const NOT_RETURNED_TEXT =
  'Excluded before the list was drawn — smart-assign returned no schedulable '
  + 'placement for them on this ticket. Not a judgement about their capability.';

/** Classify smart-assign's own rejection text. */
function classifyRejection(reason: string | null | undefined): ExclusionReason {
  if (!reason) return 'NOT_RETURNED';
  if (reason.startsWith('Tech not in context')) return 'NOT_CONSIDERED';
  if (reason.startsWith('Cannot schedule on selected date')) return 'NO_SCHEDULABLE_TIME';
  if (reason.startsWith('Bay requirements not met')) return 'BAY_INCOMPATIBLE';
  if (reason.startsWith('Suggested start time is in the past')) return 'START_IN_PAST';
  if (reason.startsWith('Proficiency too low')) return 'PROFICIENCY_GAP';
  return 'NOT_RETURNED';
}

/**
 * Which technicians were removed from consideration, and why.
 *
 * `considered` is the audit payload — every technician smart-assign looked
 * at, carrying its own `rejection_reason` — and `ranked` is what reached the
 * screen. A technician who reached the screen is never excluded, whatever the
 * audit row says: the audit is written before the browser's own filtering, so
 * a row can carry rejection text for somebody who is visibly on the list, and
 * calling that person excluded while they sit in it is simply false.
 */
export function describeExclusions(
  considered: ConsideredTechnician[],
  ranked: Array<{ technicianId: string }>,
): Exclusion[] {
  const rankedIds = new Set(ranked.map((r) => r.technicianId).filter(Boolean));
  const seen = new Set<string>();

  return (considered || [])
    .map((c) => ({
      id: c.tech_id ?? c.technicianId ?? '',
      name: c.tech_name ?? c.technicianName,
      reason: c.rejection_reason ?? null,
    }))
    .filter((c) => c.id && !rankedIds.has(c.id))
    .filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)))
    .map((c) => ({
      technicianId: c.id,
      technicianName: c.name,
      reason: classifyRejection(c.reason),
      explanation: c.reason ? `Excluded: ${c.reason}` : NOT_RETURNED_TEXT,
    }));
}
